import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  useParams,
} from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/query';
import { SEARCH } from 'constants/formFields';
import useDebouncedCallback from 'hooks/useDebouncedCallback';
import { useGetAdminUsersListQuery } from 'store/slices/adminUsers/apis/adminUsersApi';
import { usersListSchema } from 'utils/validators';

import CompanyUsersView from './CompanyUsersView';

import type { ISearchInputHandler } from 'components/TextField/interfaces/ITextField';
import type { TUsersListSchema } from 'utils/validators';
import type IParams from './interfaces/IParams';

function CompanyUsers() {
  const { clientId = '' } = useParams();
  const [params, setParams] = useState<IParams>({
    search: '',
  });

  const fetchQuery = clientId ? {
    companyId: +clientId,
    search: params.search || undefined,
  } : skipToken;

  const { data, refetch, isFetching } = useGetAdminUsersListQuery(fetchQuery);

  const { items: users = [] } = data?.data || {};

  const {
    control,
    getValues,
    reset,
  } = useForm<TUsersListSchema>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(usersListSchema),
    defaultValues: {
      [SEARCH]: params.search,
    },
  });

  const debouncedSearch = useDebouncedCallback(() => {
    const { search } = getValues();
    setParams((prev) => ({ ...prev, search }));
  });

  const onSearchChange = ({ search, onChange }: ISearchInputHandler) => {
    onChange(search);
    debouncedSearch();
  };

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    reset();
  }, [reset]);

  return (
    <CompanyUsersView
      control={control}
      users={users}
      onSearch={onSearchChange}
      isPageLoading={isFetching}
    />
  );
}

export default CompanyUsers;
